import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { loadStripe } from '@stripe/stripe-js'
import { haptic } from '@/lib/haptics'
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import { useAuthStore } from '@/store/authStore'
import { useAnalysisStore } from '@/store/analysisStore'
import { StripeTestHelper } from '@/components/dev/StripeTestHelper'
import { logError } from '@/services/errorLogger'
import { trackFunnelEvent, trackError } from '@/services/analytics'
import { createPaymentIntent } from '@/services/api'

// Initialize Stripe outside of component to avoid recreating on re-render
const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || ''
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null

// Debug: Log Stripe config status
console.log('[Stripe] Publishable key configured:', !!stripePublishableKey)

// Decorative sparkle component
const Sparkle = ({ className, size = 24, color = '#FF8C33', delay = 0 }: { className?: string; size?: number; color?: string; delay?: number }) => (
  <motion.svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    animate={{
      scale: [1, 1.3, 1],
      opacity: [0.7, 1, 0.7],
      rotate: [0, 15, 0]
    }}
    transition={{
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut",
      delay: delay
    }}
  >
    <path d="M12 0L13.5 9L22 12L13.5 15L12 24L10.5 15L2 12L10.5 9L12 0Z" />
  </motion.svg>
)

/**
 * Get user-friendly error message and suggestion based on Stripe error
 */
function getErrorInfo(error: { type?: string; code?: string; decline_code?: string; message?: string }): {
  title: string
  message: string
  suggestion: string
  emoji: string
} {
  const code = error.code || ''
  const declineCode = error.decline_code || ''

  // Card declined errors
  if (code === 'card_declined' || declineCode) {
    const declineMessages: Record<string, { title: string; message: string; suggestion: string }> = {
      'insufficient_funds': {
        title: 'Insufficient Funds',
        message: 'Your card doesn\'t have enough funds.',
        suggestion: 'Try a different card or add funds to this one.'
      },
      'lost_card': {
        title: 'Card Issue',
        message: 'This card has been reported lost.',
        suggestion: 'Please use a different card.'
      },
      'stolen_card': {
        title: 'Card Issue',
        message: 'This card cannot be used.',
        suggestion: 'Please use a different card.'
      },
      'expired_card': {
        title: 'Card Expired',
        message: 'Your card has expired.',
        suggestion: 'Please use a card with a valid expiration date.'
      },
      'incorrect_cvc': {
        title: 'Invalid Security Code',
        message: 'The security code (CVC) is incorrect.',
        suggestion: 'Check the 3-digit code on the back of your card.'
      },
      'processing_error': {
        title: 'Processing Error',
        message: 'Something went wrong while processing.',
        suggestion: 'Wait a moment and try again.'
      },
      'incorrect_number': {
        title: 'Invalid Card Number',
        message: 'The card number is incorrect.',
        suggestion: 'Double-check your card number and try again.'
      },
      'fraudulent': {
        title: 'Card Not Accepted',
        message: 'This card cannot be used for this transaction.',
        suggestion: 'Please try a different card.'
      },
      'generic_decline': {
        title: 'Card Declined',
        message: 'Your card was declined.',
        suggestion: 'Try a different card or contact your bank.'
      },
      'do_not_honor': {
        title: 'Card Declined',
        message: 'Your bank declined this transaction.',
        suggestion: 'Contact your bank or try a different card.'
      },
    }

    const declineInfo = declineMessages[declineCode] || {
      title: 'Card Declined',
      message: 'Your card was declined.',
      suggestion: 'Try a different card or contact your bank.'
    }

    return { ...declineInfo, emoji: '💳' }
  }

  // Validation errors
  if (code === 'incomplete_number' || code === 'invalid_number') {
    return {
      title: 'Invalid Card Number',
      message: 'Please check your card number.',
      suggestion: 'Make sure all 16 digits are entered correctly.',
      emoji: '🔢'
    }
  }

  if (code === 'incomplete_expiry' || code === 'invalid_expiry_year' || code === 'invalid_expiry_month') {
    return {
      title: 'Invalid Expiration',
      message: 'Please check your expiration date.',
      suggestion: 'Use the MM/YY format (e.g., 01/28).',
      emoji: '📅'
    }
  }

  if (code === 'incomplete_cvc' || code === 'invalid_cvc') {
    return {
      title: 'Invalid Security Code',
      message: 'Please check your CVC.',
      suggestion: 'It\'s the 3-digit code on the back of your card.',
      emoji: '🔒'
    }
  }

  // Network/connection errors
  if (error.type === 'api_connection_error' || code === 'network_error') {
    return {
      title: 'Connection Issue',
      message: 'We couldn\'t connect to the payment server.',
      suggestion: 'Check your internet and try again.',
      emoji: '📡'
    }
  }

  // Rate limit
  if (error.type === 'rate_limit_error') {
    return {
      title: 'Too Many Attempts',
      message: 'Please slow down a bit.',
      suggestion: 'Wait a moment before trying again.',
      emoji: '⏳'
    }
  }

  // Default fallback
  return {
    title: 'Payment Failed',
    message: error.message || 'Something went wrong.',
    suggestion: 'Please try again or use a different card.',
    emoji: '😕'
  }
}

/**
 * Checkout form with Stripe PaymentElement
 */
function CheckoutForm() {
  const stripe = useStripe()
  const elements = useElements()
  const [isProcessing, setIsProcessing] = useState(false)
  const [errorInfo, setErrorInfo] = useState<{ title: string; message: string; suggestion: string; emoji: string } | null>(null)
  const { user } = useAuthStore()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements) {
      return
    }

    setIsProcessing(true)
    setErrorInfo(null)
    trackFunnelEvent.paymentStarted(2900)

    try {
      const { error: submitError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success`,
          payment_method_data: {
            billing_details: {
              address: {
                country: 'US',
              },
            },
          },
        },
      })

      // If we reach here, payment failed (success redirects automatically)
      if (submitError) {
        const stripeError = submitError as { type?: string; code?: string; decline_code?: string; message?: string }
        setErrorInfo(getErrorInfo(stripeError))
        const declineCode = stripeError.decline_code || stripeError.code || 'unknown'
        trackFunnelEvent.paymentFailed(declineCode)
        trackError.payment(declineCode)
        logError(
          'STRIPE_PAYMENT',
          submitError.message || 'Payment confirmation failed',
          `type=${submitError.type}, code=${submitError.code}, userId=${user?.uid || 'anonymous'}`
        )
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err)
      // Try to parse as Stripe error
      const stripeError = err as { type?: string; code?: string; decline_code?: string; message?: string }
      const declineCode = stripeError.decline_code || stripeError.code || 'unknown_error'
      trackFunnelEvent.paymentFailed(declineCode)
      trackError.payment(declineCode)
      if (stripeError.code || stripeError.type || stripeError.decline_code) {
        setErrorInfo(getErrorInfo(stripeError))
      } else {
        // Check if message contains known error patterns
        const msg = errorMsg.toLowerCase()
        if (msg.includes('fraud') || msg.includes('suspicious')) {
          setErrorInfo({
            title: 'Card Not Accepted',
            message: 'This card cannot be used for this transaction.',
            suggestion: 'Please try a different card.',
            emoji: '🚫'
          })
        } else if (msg.includes('declined') || msg.includes('reject')) {
          setErrorInfo({
            title: 'Card Declined',
            message: 'Your card was declined.',
            suggestion: 'Try a different card or contact your bank.',
            emoji: '💳'
          })
        } else if (msg.includes('network') || msg.includes('connection')) {
          setErrorInfo({
            title: 'Connection Issue',
            message: 'We couldn\'t connect to the payment server.',
            suggestion: 'Check your internet and try again.',
            emoji: '📡'
          })
        } else {
          setErrorInfo({
            title: 'Payment Failed',
            message: errorMsg || 'Something went wrong.',
            suggestion: 'Please try again or use a different card.',
            emoji: '😕'
          })
        }
      }
      logError('STRIPE_PAYMENT', errorMsg, `userId=${user?.uid || 'anonymous'}`)
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-white rounded-xl p-4 border border-gray-200">
        <PaymentElement
          options={{
            layout: 'tabs',
            wallets: {
              applePay: 'auto',
              googlePay: 'auto',
            },
            fields: {
              billingDetails: {
                address: {
                  country: 'never', // Hide country - US only
                },
              },
            },
            defaultValues: {
              billingDetails: {
                address: {
                  country: 'US',
                },
              },
            },
          }}
        />
        <p className="text-xs text-gray-400 mt-2 text-center">
          🇺🇸 Available in the US only
        </p>
      </div>

      {errorInfo && (
        <motion.div
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          className="p-4 bg-[#FFF5F5] border border-[#FFCACA] rounded-[16px] text-center"
        >
          <div className="text-[32px] mb-2">{errorInfo.emoji}</div>
          <h3 className="font-semibold text-[#C53030] text-[16px] mb-1">
            {errorInfo.title}
          </h3>
          <p className="text-[#666] text-[14px] mb-2">
            {errorInfo.message}
          </p>
          <p className="text-[#46C0C6] text-[13px] font-medium">
            💡 {errorInfo.suggestion}
          </p>
        </motion.div>
      )}

      <motion.button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full h-[54px] bg-[#7ED957] rounded-[30px] text-white text-[18px] font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ fontFamily: "'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif" }}
        animate={!isProcessing ? {
          boxShadow: [
            "0 0 0 0 rgba(126, 217, 87, 0.4)",
            "0 0 0 12px rgba(126, 217, 87, 0)",
            "0 0 0 0 rgba(126, 217, 87, 0)"
          ]
        } : {}}
        transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {isProcessing ? (
          <span className="flex items-center justify-center gap-2">
            <svg
              className="animate-spin h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Processing...
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            Pay $29 →
          </span>
        )}
      </motion.button>
    </form>
  )
}

export function PaymentPage() {
  const navigate = useNavigate()
  const { user, signOut, hasPaid, setDemoMode } = useAuthStore()
  const { photoId, analysisResults, hasHDResults } = useAnalysisStore()
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Route guard: check teaser flow completion and payment status
  const hasRedirected = useRef(false)
  useEffect(() => {
    if (hasRedirected.current) return

    // Must have completed teaser flow (survey + photo)
    if (!analysisResults) {
      hasRedirected.current = true
      navigate('/survey', { replace: true })
      return
    }

    // If already paid, navigate to appropriate page
    if (hasPaid && analysisResults) {
      hasRedirected.current = true
      // If HD analysis is complete, go to report
      // Otherwise, go to full-survey to complete the flow
      if (hasHDResults()) {
        navigate('/report', { state: { showWelcomeToast: true } })
      } else {
        navigate('/full-survey')
      }
    }
  }, [hasPaid, analysisResults, hasHDResults, navigate])

  // Create PaymentIntent on mount (always initialize for testing)
  useEffect(() => {
    async function initializePayment() {
      // Check if user is logged in
      if (!user) {
        setError('Please log in with Google to test Stripe payments.')
        setLoading(false)
        return
      }

      try {
        const response = await createPaymentIntent(photoId || undefined)
        setClientSecret(response.clientSecret)
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err)

        // Check if user already paid
        if (errorMsg.includes('already paid')) {
          navigate('/full-survey')
          return
        }

        // Check for auth-related errors
        if (errorMsg.includes('Unauthorized') || errorMsg.includes('401')) {
          setError('Authentication required. Please log in with Google to test Stripe payments.')
        } else {
          setError('Failed to initialize payment. Please try again.')
        }
        logError('STRIPE_INTENT_CREATE', errorMsg, `userId=${user?.uid || 'anonymous'}`)
      } finally {
        setLoading(false)
      }
    }

    initializePayment()
  }, [photoId, navigate, user])

  // Test pass handler - skip payment for testing
  const handleTestPass = () => {
    haptic('success')
    setDemoMode(true)
    setTimeout(() => navigate('/payment-success'), 0)
  }

  return (
    <div className="min-h-screen bg-[#FFFEF1] flex flex-col">
      {/* Header */}
      <header className="bg-[#FFFEF1] py-4">
        <div className="max-w-2xl mx-auto px-4 flex items-center justify-between">
          <Link to="/" style={{ fontFamily: "'Oleo Script', cursive" }} className="text-[18px]">
            <span className="text-[#688192]">K-Beauty</span>
            <span className="text-[#46C0C6]"> AI</span>
          </Link>
          <div className="flex items-center gap-2 text-sm text-[#46C0C6]">
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            <span className="font-medium">Secure</span>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 max-w-2xl mx-auto px-4 py-6 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-5"
        >
          {/* Title with sparkles */}
          <div className="text-center mb-4 relative">
            <Sparkle className="absolute top-[-5px] left-[30px]" size={14} color="#FF8C33" delay={0} />
            <Sparkle className="absolute top-[10px] right-[40px]" size={12} color="#F596FA" delay={0.5} />
            <Sparkle className="absolute bottom-[0px] left-[50px]" size={10} color="#7182F8" delay={1} />

            <h1
              className="text-[26px] font-bold text-[#075D61] mb-2"
              style={{ fontFamily: "'Anybody', sans-serif" }}
            >
              Unlock Your Full Report
            </h1>
            <p
              className="text-[15px] text-[#656565]"
              style={{ fontFamily: "'SF Pro', -apple-system, sans-serif" }}
            >
              Your personalized K-beauty routine awaits
            </p>
          </div>

          {/* Social Proof */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.05 }}
            className="flex items-center justify-center gap-2 text-sm text-[#075D61] py-3 px-4 rounded-full"
            style={{ backgroundColor: 'rgba(200, 225, 226, 0.6)' }}
          >
            <span>🎉 Join <strong>2,500+</strong> users with perfect routines</span>
          </motion.div>

          {/* Order Summary Card */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-[20px] p-5 shadow-sm"
          >
            <h2
              className="text-[18px] text-[#E8A87C] mb-4"
              style={{ fontFamily: "'Pattaya', cursive" }}
            >
              What's included
            </h2>

            {/* What's included - compact list */}
            <div className="space-y-3 mb-5">
              {[
                { emoji: '📊', text: '14 HD skin metrics with heatmaps' },
                { emoji: '☀️', text: 'Personalized AM & PM routines' },
                { emoji: '✨', text: 'Curated K-beauty product picks' },
                { emoji: '💝', text: 'Lifetime access to your results' },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <span className="text-[18px]">{item.emoji}</span>
                  <span className="text-[14px] text-[#333] font-medium">{item.text}</span>
                </motion.div>
              ))}
            </div>

            {/* Price */}
            <div className="border-t border-gray-100 pt-4">
              <div className="flex justify-between items-center">
                <span className="text-[14px] text-[#656565]">Full Analysis Report</span>
                <div className="text-right">
                  <span className="text-[14px] text-[#999] line-through mr-2">$99</span>
                  <span className="text-[22px] font-bold text-[#FF8C33]">$29</span>
                  <span className="ml-2 text-[11px] text-white bg-[#FF6B6B] px-2 py-0.5 rounded-full">70% Off</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* User info */}
          {user && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="rounded-[16px] p-4"
              style={{ backgroundColor: 'rgba(200, 225, 226, 0.4)' }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {user.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt={user.displayName || 'User'}
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#46C0C6] flex items-center justify-center">
                      <span className="text-white font-semibold">
                        {user.displayName?.[0] || user.email?.[0] || '?'}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-[#075D61]">
                      {user.displayName || 'User'}
                    </p>
                    <p className="text-sm text-[#656565]">{user.email}</p>
                  </div>
                </div>
                <button
                  onClick={async () => {
                    await signOut()
                    navigate('/login')
                  }}
                  className="text-sm text-[#656565] hover:text-[#075D61]"
                >
                  Sign out
                </button>
              </div>
            </motion.div>
          )}

          {/* Stripe Payment Form */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            {!stripePromise ? (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm text-center"
              >
                <p className="font-medium mb-1">Payment system not configured</p>
                <p>Stripe publishable key is missing. Please use the test pass below.</p>
              </motion.div>
            ) : loading ? (
              <div className="flex items-center justify-center py-8">
                <svg
                  className="animate-spin h-8 w-8 text-[#46C0C6]"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
            ) : error ? (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm text-center"
              >
                {error}
                <button
                  onClick={() => window.location.reload()}
                  className="block mx-auto mt-2 text-red-600 underline"
                >
                  Try again
                </button>
              </motion.div>
            ) : clientSecret ? (
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret,
                  appearance: {
                    theme: 'stripe',
                    variables: {
                      colorPrimary: '#46C0C6',
                      borderRadius: '12px',
                    },
                  },
                }}
              >
                <CheckoutForm />
              </Elements>
            ) : null}

            {/* Test pass button - DEV ONLY */}
            <button
              onClick={handleTestPass}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border-2 border-dashed border-[#46C0C6]/50 bg-[#46C0C6]/10 text-[#46C0C6] font-medium hover:bg-[#46C0C6]/20 transition-colors mt-3"
            >
              <span className="text-xl">🧪</span>
              Skip Payment (Test Pass)
            </button>
          </motion.div>

          {/* Trust badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="space-y-3"
          >
            {/* Security badges */}
            <div className="flex items-center justify-center gap-6 text-[12px] text-[#46C0C6]">
              <div className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>256-bit SSL</span>
              </div>
              <div className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span>Secure Payment</span>
              </div>
            </div>

          </motion.div>

          {/* Back link */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-center pt-2"
          >
            <Link
              to="/teaser"
              className="text-[14px] text-[#46C0C6] hover:underline"
            >
              ← Back to results
            </Link>
          </motion.div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center bg-[#FFFEF1]">
        <p className="text-[12px] text-[#999]">
          Powered by{' '}
          <span className="font-medium">Stripe</span>
          {' '}• Secure payment processing
        </p>
      </footer>

      {/* Stripe Test Helper - DEV ONLY */}
      <StripeTestHelper />
    </div>
  )
}
